import { LanguageAvailable } from '@shared/services/language';
import * as z from 'zod';

/**
 * Configuration for application languages, enabling internationalization (i18n) and localization.
 *
 * Defines the default language and the collection of supported locale codes
 * for the application's language system.
 *
 * @interface LanguageConfig
 * @example Basic usage
 * ```
 * const langConfig: LanguageConfig = {
 *   default: 'fr',
 *   supported: ['fr', 'fr-BE', 'es-ES']
 * };
 * ```
 */
export interface LanguageConfig {
  /**
   * Default language ISO 639-1 code or BCP 47 language tag.
   *
   * @example 'fr', 'es-ES', 'ca-ES'
   */
  default: LanguageAvailable;

  /**
   * Array of all supported language codes following ISO 639-1 or BCP 47 format.
   *
   * @example ['fr', 'fr-BE', 'es-ES', 'ca-ES',]
   */
  supported: LanguageAvailable[];
}

/**
 * Represents the type of a brand identifier.
 */
export const brandIdSchema = z.enum(['perene', 'socooc', 'mobalpa', 'hygena']);
export type BrandId = z.infer<typeof brandIdSchema>;

/**
 * Configuration settings for a specific domain, including brand identity and language preferences.
 *
 * This interface defines domain-specific parameters such as branding theme
 * and language configuration for multi-tenant or multi-domain applications.
 *
 * @interface DomainConfig
 * @example Domain configuration with brand and languages
 * ```
 * const config: DomainConfig = {
 *   brand: 'socooc',
 *   language: {
 *     default: 'es-ES',
 *     supported: ['es-ES', 'ca-ES']
 *   }
 * };
 * ```
 */
export interface DomainConfig {
  /**
   * Visual branding theme identifier restricted to specific brand values.
   *
   * @remarks Limited to three predefined brand themes.
   */
  brand: BrandId;

  /**
   * Language settings for the domain.
   *
   * @see {@link LanguageConfig}
   */
  language: LanguageConfig;
}

/**
 * Type alias mapping domain strings to their corresponding configuration objects.
 *
 * Provides a strongly-typed dictionary structure where each domain key
 * maps to a `DomainConfig` instance, enabling multi-domain configuration management.
 *
 * @typeParam string - Domain identifier (e.g., hostname or URL)
 * @typeParam DomainConfig - Domain-specific configuration
 * @example Multi-domain configuration mapping
 * ```
 * const domainMap: DomainMap = {
 *   'perene.fr': {
 *     brand: 'perene',
 *     language: { default: 'fr', supported: ['fr'] }
 *   },
 *   'socooc.es': {
 *     brand: 'socooc',
 *     language: { default: 'es-ES', supported: ['es-ES', 'ca-ES'] }
 *   }
 * };
 * console.log(domainMap['perene.fr'].brand); // 'perene'
 * ```
 */
export type DomainMap = Record<string, DomainConfig>;

/**
 * Retrieves the display label for a given brand identifier.
 *
 * @param brandId - The unique identifier of the brand.
 * @returns The formatted display label corresponding to the brand ID.
 *
 * @example
 * ```typescript
 * getBrandLabel('socooc'); // Returns "SoCoo'c"
 * ```
 */
export function getBrandLabel(brandId: BrandId): string {
  switch (brandId) {
    case 'perene':
      return 'Perene';
    case 'socooc':
      return "SoCoo'c";
    case 'mobalpa':
      return 'Mobalpa';
    case 'hygena':
      return 'Hygena';
    default:
      return brandId;
  }
}
